// App-wide "confirm before leaving" guard for in-app SPA navigation.
//
// Why this instead of react-router v6's `useBlocker`: this app renders with a
// plain `BrowserRouter` (see App.tsx), not a data router
// (`createBrowserRouter`/`createRoutesFromElements`). `useBlocker` (and
// `unstable_usePrompt`) in react-router-dom v6 only work under a data router
// - called under a plain `BrowserRouter` they throw ("useBlocker must be used
// within a data router") rather than quietly no-op, so they are not usable at
// all in this app's current routing setup without migrating off
// `BrowserRouter` entirely (a much bigger, unrelated change).
//
// Instead: every in-app navigation in this codebase goes through
// react-router's `navigate()` (see Navbar.tsx - the main menu calls
// `navigate(path)` for every item, no `<Link>` components are used for
// primary nav), and react-router's underlying `history` package implements
// `navigate()` by calling the real `window.history.pushState` at the moment
// it's invoked. Patching `pushState` once, here, at module scope, intercepts
// every programmatic in-app navigation app-wide with no changes needed at
// each call site (Navbar, a Cancelar button, breadcrumbs, ...) and no risk of
// a future call site forgetting to check a guard.
//
// Scope/limits, on purpose:
// - This only covers *pushState*-driven navigation (clicking something that
//   calls `navigate(...)`). It deliberately does NOT try to trap the
//   browser's own Back/Forward buttons (`popstate`) - reliably intercepting
//   that requires pushing a dummy history entry and juggling it inside the
//   popstate handler, a separate and much gnarlier problem than what this
//   guard is built for.
// - Tab-close/refresh/typed-URL-then-Enter navigation never goes through
//   `pushState` at all, so this module can't and doesn't cover it - that's
//   handled separately via a `beforeunload` listener in
//   `hooks/useUnsavedChangesGuard.ts`.

let blocked = false;
let confirmMessage =
  'Tenés cambios sin guardar en este presupuesto. Si salís ahora los vas a perder. ¿Querés continuar?';

let patched = false;

function patchHistoryOnce() {
  if (patched) return;
  patched = true;

  const originalPushState = window.history.pushState.bind(window.history);

  window.history.pushState = function (
    this: History,
    ...args: Parameters<History['pushState']>
  ) {
    if (blocked) {
      const proceed = window.confirm(confirmMessage);
      if (!proceed) {
        // Usuario canceló: no navegamos. La URL/estado no cambian.
        return;
      }
    }
    return originalPushState(...args);
  };
}

patchHistoryOnce();

/**
 * Activa o desactiva el guard de navegación bloqueada. `message` (opcional)
 * reemplaza el texto del confirm nativo para la próxima vez que se dispare.
 */
export function setNavigationBlocked(isBlocked: boolean, message?: string): void {
  blocked = isBlocked;
  if (message) {
    confirmMessage = message;
  }
}
